'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

export function NavigationModal() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const t = useTranslations('Navigation');
  const tShopNav = useTranslations('shopNav');

  const navigationItems = [
    {
      href: '/',
      title: t('home'),
      description: t('homeDescription'),
      color: 'var(--pub-blue)',
      colorDim: 'rgba(59,130,246,0.1)',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      paths: ['/'],
    },
    {
      href: '/instructions',
      title: t('instructions'),
      description: t('instructionsDescription'),
      color: 'var(--pub-green)',
      colorDim: 'rgba(34,197,94,0.1)',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
          <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
        </svg>
      ),
      paths: ['/instructions'],
    },
    {
      href: '/docs',
      title: t('docs'),
      description: t('docsDescription'),
      color: 'var(--pub-green)',
      colorDim: 'rgba(34,197,94,0.08)',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
      paths: ['/docs'],
    },
    {
      href: '/map',
      title: t('map'),
      description: t('mapDescription'),
      color: 'rgb(245,158,11)',
      colorDim: 'rgba(245,158,11,0.1)',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
      paths: ['/map'],
    },
    {
      href: '/countdown',
      title: t('countdown'),
      description: t('countdownDescription'),
      color: 'rgb(129,140,248)',
      colorDim: 'rgba(129,140,248,0.1)',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      paths: ['/countdown'],
    },
  ];

  const shopSubItems = [
    {
      href: '/shop',
      label: tShopNav('viewProducts'),
      description: tShopNav('viewProductsDesc'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a4 4 0 00-8 0v2" />
        </svg>
      ),
    },
    {
      href: 'https://absoluteeclipse.eu',
      label: tShopNav('visitStore'),
      description: 'absoluteeclipse.eu',  // domain name, not translated
      external: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
        </svg>
      ),
    },
    {
      href: 'https://www.amazon.de/stores/AbsoluteEclipse/page/DF8A215C-90EB-48D7-A75B-1F4E0801B479',
      label: tShopNav('visitAmazon'),
      description: tShopNav('amazonDesc'),
      external: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
        </svg>
      ),
    },
  ];

  const shopActive = (() => {
    const cleanPathname = pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/';
    return cleanPathname.startsWith('/shop');
  })();

  const isActive = (item: (typeof navigationItems)[0]) => {
    const cleanPathname = pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/';
    return item.paths.some((path) => {
      if (path === '/') return cleanPathname === '/';
      return cleanPathname.startsWith(path);
    });
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center justify-center cursor-pointer transition-colors duration-200"
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          border: '1px solid var(--pub-border, rgba(0,0,0,0.1))',
          background: 'var(--pub-surface-2, #f8fafc)',
          color: 'var(--pub-text-secondary, #6b7280)',
        }}
        aria-label="Open Navigation"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      </button>

      {/* Backdrop + Sheet */}
      {open && (
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
            onClick={() => setOpen(false)}
          />

          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 flex flex-col"
            style={{
              background: 'var(--pub-surface, #ffffff)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              maxHeight: '85vh',
              overflow: 'hidden',
              animation: 'pub-slide-up 0.3s ease-out',
            }}
          >
            {/* Handle + header */}
            <div className="flex flex-col items-center pt-3 pb-4 flex-shrink-0">
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 100,
                  background: 'var(--pub-border, rgba(0,0,0,0.1))',
                  marginBottom: 16,
                }}
              />
              <span
                style={{
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--pub-text-muted)',
                }}
              >
                Navigation
              </span>
            </div>

            {/* Items */}
            <div
              className="flex flex-col overflow-y-auto min-h-0"
              style={{ padding: '0 16px', paddingBottom: 'max(32px, env(safe-area-inset-bottom, 32px))' }}
            >
              {navigationItems.map((item) => {
                const active = isActive(item);

                return (
                  <Link
                    key={item.title}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 transition-colors duration-150"
                    style={{
                      padding: '14px 14px',
                      borderRadius: 14,
                      background: active ? item.colorDim : 'transparent',
                      textDecoration: 'none',
                      marginBottom: 4,
                    }}
                  >
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 12,
                        background: item.colorDim,
                        border: active ? `1px solid ${item.color}` : '1px solid transparent',
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div style={{ fontSize: 14, fontWeight: 600, color: active ? item.color : 'var(--pub-text-primary)' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                        {item.description}
                      </div>
                    </div>
                    <div className="flex-shrink-0" style={{ color: active ? item.color : 'var(--pub-text-muted)' }}>
                      {active ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ opacity: 0.5 }}>
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      )}
                    </div>
                  </Link>
                );
              })}

              {/* Shop & Store section */}
              <div
                style={{
                  marginTop: 4,
                  borderRadius: 16,
                  background: shopActive ? 'rgba(245,158,11,0.06)' : 'var(--pub-surface-2, #f8fafc)',
                  border: '1px solid var(--pub-border, rgba(0,0,0,0.06))',
                  overflow: 'hidden',
                }}
              >
                {/* Section header */}
                <div
                  className="flex items-center gap-3"
                  style={{ padding: '14px 14px 6px' }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'rgba(245,158,11,0.1)',
                      border: shopActive ? '1px solid rgb(245,158,11)' : '1px solid transparent',
                      color: 'rgb(245,158,11)',
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <circle cx="9" cy="21" r="1" />
                      <circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: shopActive ? 'rgb(245,158,11)' : 'var(--pub-text-primary)' }}>
                      {t('shop')}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 1 }}>
                      {t('shopDescription')}
                    </div>
                  </div>
                </div>

                {/* Sub-items */}
                <div style={{ padding: '6px 10px 10px' }}>
                  {shopSubItems.map((sub) => {
                    const Comp = sub.external ? 'a' : Link;
                    const props = sub.external
                      ? { href: sub.href, target: '_blank' as const, rel: 'noopener noreferrer' }
                      : { href: sub.href };

                    return (
                      <Comp
                        key={sub.label}
                        {...(props as any)}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 transition-colors duration-150"
                        style={{
                          padding: '10px 10px',
                          borderRadius: 10,
                          textDecoration: 'none',
                        }}
                      >
                        <div
                          className="flex items-center justify-center flex-shrink-0"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: 'rgba(245,158,11,0.08)',
                            color: 'rgb(245,158,11)',
                          }}
                        >
                          {sub.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--pub-text-primary)' }}>
                            {sub.label}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--pub-text-muted)', marginTop: 1 }}>
                            {sub.description}
                          </div>
                        </div>
                        <div className="flex-shrink-0" style={{ color: 'var(--pub-text-muted)' }}>
                          {sub.external ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" style={{ opacity: 0.5 }}>
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          )}
                        </div>
                      </Comp>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slide-up animation */}
      <style jsx global>{`
        @keyframes pub-slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}
